/**
 * Renderiza las opciones de respuesta de un anuncio.
 *
 * El layout depende del `value` semántico de cada opción (no de la cantidad ni
 * del display_order). El dashboard fuerza estos valores a través de plantillas
 * (ver constants/plans.ts en dashboard-cs-follow → ANNOUNCEMENT_OPTION_TEMPLATES).
 *
 * Categorías:
 *   - reaction (like / dislike / yes / no) → grid 2-col compacto, ícono + label sm
 *   - cta (link / close / custom)          → apilados, full-width, label visible
 *
 * Variantes visuales:
 *   - modal  → reactions arriba en grid + CTAs apilados debajo
 *   - banner → todo inline (ignora la separación, banner es compacto)
 *   - card   → reactions en fila + CTAs apilados (similar a modal)
 */

import type { ReactNode } from 'react';
import { Button } from '@plick/ui';
import {
  IconBrandWhatsapp,
  IconCalendarPlus,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconExternalLink,
  IconInfoCircle,
  IconMoodNeutral,
  IconRocket,
  IconThumbDownFilled,
  IconThumbUpFilled,
} from '@tabler/icons-react';
import { AnnouncementOption, AnnouncementUserResponse } from '@interfaces/general';
import { handleOptionAction } from './handleOptionAction';
import type { DismissPayload } from '../CommunicationsModal/hooks/useRespondAnnouncement';

type Variant = 'modal' | 'banner' | 'card';
type Category = 'reaction' | 'cta';

interface ValueDecoration {
  icon: ReactNode;
  category: Category;
  /** Color usado en el botón (background en reactions, accent en CTAs). */
  color: string;
}

const REACTION_GREEN = '#22C55E';
const REACTION_RED = '#EF4444';
// Reacciones que no son positivo/negativo (ej. "ya lo resuelvo de otra forma"):
// pintarlas rojas sesgaría la lectura como rechazo cuando son neutras.
const REACTION_NEUTRAL = '#6B7280';

const VALUE_DECORATIONS: Record<string, ValueDecoration> = {
  like: {
    icon: <IconThumbUpFilled width={18} height={18} />,
    category: 'reaction',
    color: REACTION_GREEN,
  },
  dislike: {
    icon: <IconThumbDownFilled width={18} height={18} />,
    category: 'reaction',
    color: REACTION_RED,
  },
  interested: {
    icon: <IconThumbUpFilled width={18} height={18} />,
    category: 'reaction',
    color: REACTION_GREEN,
  },
  not_interested: {
    icon: <IconThumbDownFilled width={18} height={18} />,
    category: 'reaction',
    color: REACTION_RED,
  },
  yes: {
    icon: <IconCircleCheckFilled width={18} height={18} />,
    category: 'reaction',
    color: REACTION_GREEN,
  },
  no: {
    icon: <IconCircleXFilled width={18} height={18} />,
    category: 'reaction',
    color: REACTION_RED,
  },
  already_solved: {
    icon: <IconMoodNeutral width={18} height={18} />,
    category: 'reaction',
    color: REACTION_NEUTRAL,
  },
  link: {
    icon: <IconExternalLink width={18} height={18} />,
    category: 'cta',
    color: '',
  },
  schedule_demo: {
    icon: <IconCalendarPlus width={18} height={18} />,
    category: 'cta',
    color: '',
  },
  whatsapp_contact: {
    icon: <IconBrandWhatsapp width={18} height={18} />,
    category: 'cta',
    color: '',
  },
  activate_now: {
    icon: <IconRocket width={18} height={18} />,
    category: 'cta',
    color: '',
  },
  tell_me_more: {
    icon: <IconInfoCircle width={18} height={18} />,
    category: 'cta',
    color: '',
  },
};

const decorationFor = (value: string): ValueDecoration | null => VALUE_DECORATIONS[value] ?? null;
const categoryOf = (value: string): Category => decorationFor(value)?.category ?? 'cta';

/**
 * Orden visual fijo para reactions: las negativas a la izquierda, las positivas
 * a la derecha. Independiente del display_order que CS configure.
 * Cualquier value no listado va al final.
 */
const REACTION_ORDER: Record<string, number> = {
  dislike: 0,
  not_interested: 1,
  no: 2,
  yes: 3,
  interested: 4,
  like: 5,
  // 'already_solved' va al final: no es voto, es "no aplica para mi".
  already_solved: 6,
};
const reactionWeight = (value: string): number => REACTION_ORDER[value] ?? 99;

interface Props {
  options: AnnouncementOption[];
  announcementId: string;
  userResponse: AnnouncementUserResponse | null;
  bgColor: string;
  textColor: string;
  isSubmitting: boolean;
  /** Id de la opción que está enviando ahora — para mostrar spinner solo en esa */
  submittingOptionId: string | null;
  /** Cuando true, las opciones con action_type='close' se ocultan (no se puede
   *  cerrar el anuncio sin responder, contrato de is_persistent). */
  isPersistent: boolean;
  variant: Variant;
  respond: (announcementId: string, optionId: string, value: string) => Promise<unknown>;
  onComplete: () => void;
  dismiss?: (announcementId: string, payload: DismissPayload) => Promise<void>;
}

interface RowProps {
  option: AnnouncementOption;
  announcementId: string;
  userResponse: AnnouncementUserResponse | null;
  bgColor?: string;
  textColor?: string;
  isSubmitting: boolean;
  submittingOptionId: string | null;
  isPersistent: boolean;
  respond: (announcementId: string, optionId: string, value: string) => Promise<unknown>;
  onComplete: () => void;
  dismiss?: (announcementId: string, payload: DismissPayload) => Promise<void>;
}

const ReactionButton = ({
  option,
  announcementId,
  userResponse,
  isSubmitting,
  submittingOptionId,
  isPersistent,
  respond,
  onComplete,
  dismiss,
}: RowProps) => {
  const decoration = decorationFor(option.value);
  const isSelected = userResponse?.option_id === option.id;
  const otherSelected = userResponse?.option_id && !isSelected;
  const color = decoration?.color ?? REACTION_GREEN;
  const isMine = submittingOptionId === option.id;

  return (
    <div className="flex-1 min-w-0 [&>button]:w-full" style={{ opacity: otherSelected ? 0.4 : 1 }}>
      <Button.Custom
        text={option.label}
        leftIcon={decoration?.icon}
        onClick={() => handleOptionAction({ option, announcementId, isPersistent, respond, onComplete, dismiss })}
        isLoading={isMine}
        disabled={isSubmitting && !isMine}
        backgroundColor={color}
        borderColor={color}
        color="#FFFFFF"
      />
    </div>
  );
};

const CtaButton = ({
  option,
  announcementId,
  isSubmitting,
  submittingOptionId,
  isPersistent,
  respond,
  onComplete,
  dismiss,
}: RowProps) => {
  const isCloseAction = option.action_type === 'close';
  const isMine = submittingOptionId === option.id;
  const decoration = decorationFor(option.value);
  // close → outline (Secondary); el resto → llenado (Primary)
  const ButtonVariant = isCloseAction ? Button.Secondary : Button.Primary;

  return (
    <div className="w-full [&>button]:w-full">
      <ButtonVariant
        text={option.label}
        leftIcon={decoration?.icon}
        onClick={() => handleOptionAction({ option, announcementId, isPersistent, respond, onComplete, dismiss })}
        isLoading={isMine}
        disabled={isSubmitting && !isMine}
      />
    </div>
  );
};

const BannerButton = ({
  option,
  announcementId,
  isSubmitting,
  submittingOptionId,
  isPersistent,
  respond,
  onComplete,
  dismiss,
}: RowProps) => {
  const isCloseAction = option.action_type === 'close';
  const isMine = submittingOptionId === option.id;
  const decoration = decorationFor(option.value);
  const ButtonVariant = isCloseAction ? Button.Secondary : Button.Primary;

  return (
    <div className="[&>button]:h-8 [&>button]:px-3 [&>button]:text-xs">
      <ButtonVariant
        text={option.label}
        leftIcon={decoration?.icon}
        onClick={() => handleOptionAction({ option, announcementId, isPersistent, respond, onComplete, dismiss })}
        isLoading={isMine}
        disabled={isSubmitting && !isMine}
      />
    </div>
  );
};

export const AnnouncementOptionButtons = ({
  options,
  announcementId,
  userResponse,
  bgColor,
  textColor,
  isSubmitting,
  submittingOptionId,
  isPersistent,
  variant,
  respond,
  onComplete,
  dismiss,
}: Props) => {
  if (!options || options.length === 0) return null;

  // Anuncios persistentes ocultan el boton 'close' (no se puede cerrar sin
  // responder). handleOptionAction tambien lo defiende, pero esto evita
  // mostrar un boton inerte.
  const sized = [...options]
    .filter((o) => !(isPersistent && o.action_type === 'close'))
    .sort((a, b) => a.display_order - b.display_order);

  // Banner es compacto: ignoramos la separación reaction/cta y renderizamos todo
  // inline con el estilo clásico.
  if (variant === 'banner') {
    return (
      <div className="flex flex-row gap-2 flex-wrap items-center">
        {sized.map((option) => (
          <BannerButton
            key={option.id}
            option={option}
            announcementId={announcementId}
            userResponse={userResponse}
            bgColor={bgColor}
            textColor={textColor}
            isSubmitting={isSubmitting}
            respond={respond}
            onComplete={onComplete}
            submittingOptionId={submittingOptionId}
            isPersistent={isPersistent}
            dismiss={dismiss}
          />
        ))}
      </div>
    );
  }

  // Modal y card: split en dos contenedores.
  const reactions = sized
    .filter((o) => categoryOf(o.value) === 'reaction')
    .sort((a, b) => reactionWeight(a.value) - reactionWeight(b.value));
  const ctas = sized.filter((o) => categoryOf(o.value) === 'cta');

  // Layout reactions:
  //   1 → 1 col full width
  //   2 → 2 cols side-by-side (comportamiento histórico)
  //   3 → 2 cols + el 3ro col-span-2 abajo (evita 3 botones apretados a 1/3 cada uno)
  //   4 → 2x2 (mismo grid-cols-2)
  // Las clases del grid son estáticas para que Tailwind JIT no las strippee.
  const reactionGridClass = reactions.length === 1 ? 'grid-cols-1' : 'grid-cols-2';

  return (
    <div className="flex flex-col gap-3 w-full">
      {reactions.length > 0 && (
        <div className={`grid items-center gap-2 w-full ${reactionGridClass}`}>
          {reactions.map((option, idx) => (
            <div key={option.id} className={reactions.length === 3 && idx === 2 ? 'col-span-2' : undefined}>
              <ReactionButton
                option={option}
                announcementId={announcementId}
                userResponse={userResponse}
                isSubmitting={isSubmitting}
                respond={respond}
                onComplete={onComplete}
                submittingOptionId={submittingOptionId}
                isPersistent={isPersistent}
                dismiss={dismiss}
              />
            </div>
          ))}
        </div>
      )}

      {ctas.length > 0 && (
        <div className="flex flex-col gap-2">
          {ctas.map((option) => (
            <CtaButton
              key={option.id}
              option={option}
              announcementId={announcementId}
              userResponse={userResponse}
              bgColor={bgColor}
              textColor={textColor}
              isSubmitting={isSubmitting}
              respond={respond}
              onComplete={onComplete}
              submittingOptionId={submittingOptionId}
              isPersistent={isPersistent}
              dismiss={dismiss}
            />
          ))}
        </div>
      )}
    </div>
  );
};
